import { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Divider from "@mui/material/Divider";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import api from "api/axios";

function Feedback({ open, onClose, reservation }) {
  const [form, setForm] = useState({
    nb_participants: "",
    nb_nouveaux: "",
    duree_heures: "",
    cout_total: "",
    recettes: "",
    axe_amelioration: "",
    observations: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const benefice = (parseFloat(form.recettes) || 0) - (parseFloat(form.cout_total) || 0);

  useEffect(() => {
    if (open && reservation) {
      // Charger le feedback existant
      api.get(`/feedbacks/${reservation.id}`)
        .then((res) => {
          if (res.data) {
            setForm({
              nb_participants: res.data.nb_participants || "",
              nb_nouveaux: res.data.nb_nouveaux || "",
              duree_heures: res.data.duree_heures || "",
              cout_total: res.data.cout_total || "",
              recettes: res.data.recettes || "",
              axe_amelioration: res.data.axe_amelioration || "",
              observations: res.data.observations || "",
            });
          }
        })
        .catch(console.error);
    }
  }, [open, reservation]);

  const handleClose = () => {
    setSuccess(false);
    setError("");
    onClose();
  };

  const handleSubmit = async () => {
    setError("");
    setLoading(true);
    try {
      await api.post(`/feedbacks/${reservation.id}`, form);
      setSuccess(true);
      setTimeout(() => handleClose(), 2000);
    } catch (err) {
      setError(err.response?.data?.message || "Erreur serveur.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        Retour — {reservation?.directeur_ville} — {reservation?.occasion}
      </DialogTitle>
      <DialogContent>
        {success ? (
          <MDBox textAlign="center" py={3}>
            <MDTypography variant="h6" sx={{ color: "#43a047" }}>
              Retour enregistré avec succès !
            </MDTypography>
          </MDBox>
        ) : (
          <MDBox display="flex" flexDirection="column" gap={2} mt={1}>

            {/* Comparaison participants */}
            {reservation?.nb_personnes && (
              <MDBox sx={{ bgcolor: "#e3f2fd", borderRadius: 2, p: 2 }}>
                <MDTypography variant="body2" fontWeight="medium" sx={{ color: "#1565c0" }}>
                  Nombre estimé : {reservation.nb_personnes} personnes
                </MDTypography>
              </MDBox>
            )}

            {/* Informations générales */}
            <MDTypography variant="subtitle2" fontWeight="bold" color="text">
              Informations générales
            </MDTypography>
            <MDBox display="flex" gap={2}>
              <TextField
                label="Nombre de participants (réel)"
                type="number"
                fullWidth
                value={form.nb_participants}
                onChange={(e) => setForm({ ...form, nb_participants: e.target.value })}
              />
              <TextField
                label="Dont nouveaux"
                type="number"
                fullWidth
                value={form.nb_nouveaux}
                onChange={(e) => setForm({ ...form, nb_nouveaux: e.target.value })}
              />
            </MDBox>
            <TextField
              label="Durée réelle de la fête (heures)"
              type="number"
              fullWidth
              inputProps={{ step: 0.5 }}
              value={form.duree_heures}
              onChange={(e) => setForm({ ...form, duree_heures: e.target.value })}
            />

            <Divider />

            {/* Finances */}
            <MDTypography variant="subtitle2" fontWeight="bold" color="text">
              Finances
            </MDTypography>
            <MDBox display="flex" gap={2}>
              <TextField
                label="Coût total (€)"
                type="number"
                fullWidth
                value={form.cout_total}
                onChange={(e) => setForm({ ...form, cout_total: e.target.value })}
              />
              <TextField
                label="Recettes (€)"
                type="number"
                fullWidth
                value={form.recettes}
                onChange={(e) => setForm({ ...form, recettes: e.target.value })}
              />
            </MDBox>
            {(form.cout_total || form.recettes) && (
              <MDBox
                sx={{
                  bgcolor: benefice >= 0 ? "#e8f5e9" : "#ffebee",
                  borderRadius: 2,
                  p: 1.5,
                }}
              >
                <MDTypography
                  variant="body2"
                  fontWeight="bold"
                  sx={{ color: benefice >= 0 ? "#2e7d32" : "#c62828" }}
                >
                  Bénéfice / Déficit : {benefice >= 0 ? "+" : ""}{benefice.toFixed(2)} €
                </MDTypography>
              </MDBox>
            )}

            <Divider />

            {/* Groupe de chants */}
            <MDTypography variant="subtitle2" fontWeight="bold" color="text">
              Groupe de chants
            </MDTypography>
            <TextField
              label="Axe d'amélioration"
              fullWidth
              multiline
              rows={3}
              value={form.axe_amelioration}
              onChange={(e) => setForm({ ...form, axe_amelioration: e.target.value })}
            />

            <Divider />

            {/* Observations */}
            <MDTypography variant="subtitle2" fontWeight="bold" color="text">
              Observations générales
            </MDTypography>
            <TextField
              label="Observations"
              fullWidth
              multiline
              rows={3}
              value={form.observations}
              onChange={(e) => setForm({ ...form, observations: e.target.value })}
            />

            {error && (
              <MDTypography variant="caption" color="error">{error}</MDTypography>
            )}
          </MDBox>
        )}
      </DialogContent>
      <DialogActions>
        <MDButton onClick={handleClose} color="secondary">Annuler</MDButton>
        {!success && (
          <MDButton onClick={handleSubmit} color="info" disabled={loading}>
            {loading ? "Enregistrement..." : "Enregistrer le retour"}
          </MDButton>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default Feedback;
